import React, { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "../../UIElements/Icon";
import { useBusinessesLite } from "../../../../hooks";
import "./BusinessDropdown.css";

const BusinessDropdown = ({ isOpen, selectedBrand, onBrandSelect }) => {
  const { t } = useTranslation(["navigation", "common"]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const listRef = useRef(null);

  const {
    businesses,
    isLoading,
    isError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useBusinessesLite({
    enabled: isOpen,
    searchTerm: debouncedSearch,
  });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset search when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      setDebouncedSearch("");
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  // Update highlighted index when businesses change
  useEffect(() => {
    if (businesses.length === 0) {
      setHighlightedIndex(-1);
      return;
    }
    const selectedIndex = businesses.findIndex(
      (b) => b?.id === selectedBrand?.id,
    );
    setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [businesses.length, selectedBrand?.id]);

  // Infinite scroll
  const handleScroll = useCallback(() => {
    if (!listRef.current || isFetchingNextPage || !hasNextPage) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  const handleListKeyDown = (event) => {
    if (businesses.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((prev) =>
        prev < 0 ? 0 : (prev + 1) % businesses.length,
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((prev) =>
        prev <= 0 ? businesses.length - 1 : prev - 1,
      );
      return;
    }

    if (event.key === "Enter" && highlightedIndex >= 0) {
      event.preventDefault();
      onBrandSelect(businesses[highlightedIndex]);
    }
  };

  if (!isOpen) return null;

  if (isError) {
    return (
      <div className="business-dropdown business-dropdown--error">
        <div className="business-dropdown__content">
          <div className="business-dropdown__error-header">
            <Icon
              name="AlertCircle"
              className="business-dropdown__error-icon"
            />
            <span className="business-dropdown__error-title">
              {t("topbar.businessDropdown.failedToLoadBusinesses", {
                ns: "navigation",
              })}
            </span>
          </div>
          <p className="business-dropdown__error-message">
            {t("topbar.businessDropdown.errorMessage", { ns: "navigation" })}
          </p>
          <button
            onClick={() => refetch()}
            className="business-dropdown__retry-button"
          >
            {t("actions.retry", { ns: "common" })}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="business-dropdown business-dropdown--success">
      <div className="business-dropdown__content">
        <div className="business-dropdown__search-wrapper">
          <Icon name="Search" className="business-dropdown__search-icon" />
          <input
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            onKeyDown={handleListKeyDown}
            className="business-dropdown__search-input"
            placeholder={t("topbar.businessDropdown.searchPlaceholder", {
              ns: "navigation",
            })}
            aria-label={t("topbar.businessDropdown.searchLabel", {
              ns: "navigation",
            })}
          />
        </div>

        {isLoading && businesses.length === 0 ? (
          <div className="business-dropdown__loading">
            <div className="business-dropdown__spinner"></div>
            <span className="business-dropdown__loading-text">
              {t("topbar.businessDropdown.loadingBusinesses", {
                ns: "navigation",
              })}
            </span>
          </div>
        ) : businesses.length > 0 ? (
          <div
            ref={listRef}
            className="business-dropdown__list"
            role="listbox"
            onScroll={handleScroll}
            aria-activedescendant={
              highlightedIndex >= 0
                ? `business-dropdown-option-${businesses[highlightedIndex]?.id}`
                : undefined
            }
          >
            {businesses.map((brand, index) => (
              <button
                key={brand.id}
                id={`business-dropdown-option-${brand.id}`}
                onClick={() => onBrandSelect(brand)}
                onMouseEnter={() => setHighlightedIndex(index)}
                onKeyDown={handleListKeyDown}
                className={`business-dropdown__item ${
                  selectedBrand?.id === brand.id
                    ? "business-dropdown__item--selected"
                    : "business-dropdown__item--unselected"
                } ${
                  highlightedIndex === index
                    ? "business-dropdown__item--active"
                    : ""
                }`}
                role="option"
                aria-selected={selectedBrand?.id === brand.id}
                tabIndex={0}
              >
                <div className="business-dropdown__item-content">
                  {selectedBrand?.id === brand.id && (
                    <Icon
                      name="Check"
                      className="business-dropdown__check-icon"
                    />
                  )}
                  <div className="business-dropdown__item-text">
                    <div className="business-dropdown__item-name">
                      {brand.name}
                    </div>
                    {brand.address && (
                      <div className="business-dropdown__item-address">
                        {brand.address}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
            {isFetchingNextPage && (
              <div className="business-dropdown__loading-more">
                <div className="business-dropdown__spinner business-dropdown__spinner--small"></div>
              </div>
            )}
          </div>
        ) : (
          <div className="business-dropdown__no-results" role="status">
            {t("topbar.businessDropdown.noSearchResults", {
              ns: "navigation",
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export { BusinessDropdown };
